import { Component, Input, OnChanges, OnDestroy, ChangeDetectionStrategy } from '@angular/core';

import { AppState } from '../app.state';

import { PipeFunctions } from '../pipes.module/_pipe.functions';
import { Router } from '@angular/router';
import { BuergschaftsAntragTypes } from '../model.module/bgsch-antrag-type.model';
import { Permission } from '../model.module/constant/rights.enum';
import { AuthService } from '../services.module/auth.service';
import { ClientDownloadService } from '../services.module/client-download.service';
import { MessageObservable } from '../services.module/message.observable';
import { MessageModel } from '../model.module/modals.model';
import { Buergschein, BuergscheinLight } from '../../generated/api/kreditportalBackendAPI.schemas';
import { BuergscheineService } from '../providers.module/buergscheine.service';

@Component({
    selector: '[elem-bgsch-table-sub]',
    templateUrl: './bgsch-table-add.component.html',
    styleUrls: ['./table.component.less'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class BurgscheinTableSubComponent implements OnChanges, OnDestroy {

    @Input()
    open: boolean;

    @Input()
    buergschein: BuergscheinLight;

    private readonly emptyVertrag = 'Vertrag';

    private readonly selectedVertrag: string;

    model: Buergschein;

    rueckforderungLocked = false;

    constructor(
        private readonly clientDownloadService: ClientDownloadService,
        private readonly messageObservable: MessageObservable,
        private readonly state: AppState,
        private readonly router: Router,
        private readonly authService: AuthService,
        private readonly buergscheineService: BuergscheineService
    ) {
        this.selectedVertrag = PipeFunctions.formatVsnr(this.state.lastVertragDisplayed.vsnrNumerisch) || this.emptyVertrag;
    }

    ngOnChanges() {
        if (!this.open) {
            return;
        }

        const vsnr = this.selectedVertrag;
        const bsnr = this.buergschein['buergscheinId'];

        this.buergscheineService.getBuergschein(vsnr, bsnr).subscribe(buergschein => {
            this.model = buergschein;
            this.fillEmptyModel();
        });
    }

    fillEmptyModel(): void {
        this.model.auftrag1 = this.model.auftrag1 || '-';
        this.model.ausstellungsdatum = this.model.ausstellungsdatum || '-';
        this.model.buergschaftsArt = this.model.buergschaftsArt || 'UNKNOWN';
        this.model.auftraggeberinfo1 = this.model.auftraggeberinfo1 || '-';
    }

    ngOnDestroy() {
        this.model = null;
        this.buergschein = null;
    }

    canModifyBs(): boolean {
        return (this.authService.getRole() & Permission.READ_CREATE_BS) === Permission.READ_CREATE_BS
            && this.buergschein.statusanzeigeBuergschaft === 'AUSGESTELLT';
    }

    createBuergscheinWithVorlage(aendern = false): void {
        const alter = aendern ? 1 : 0;
        const rigidVsnr = PipeFunctions.formatVsnrRigid(this.state.lastVertragDisplayed.vsnrFormatiert);

        this.router.navigate(
            ['kreditportal/buergschaften', 'create-buergschein'],
            {
                queryParams: {
                    vsnr: rigidVsnr,
                    bgsch: this.buergschein['buergscheinId'],
                    alter
                },
            },
        );
    }

    translateBuergscheinArt(abbr: string): string {
        if (!abbr) {
            return '';
        }

        return BuergschaftsAntragTypes.findByBuergschaftsart(abbr).label;
    }

    downloadBuergschein(original = false): void {
        const vsnr = this.selectedVertrag;
        const bsnr = this.buergschein['buergscheinId'];

        this.clientDownloadService.openPdfInNewTab(
            this.buergscheineService.getBuergscheinDokument(vsnr, bsnr, original),
            () => alert('Für die Bürgschaft ist noch kein Dokument verfügbar.')
        );
    }

    rueckforderungBuergschaft(): void {
        const vsnr = this.selectedVertrag;
        const bsnr = this.buergschein['buergscheinId'];

        this.rueckforderungLocked = true;

        const localStorageKey = `rueckforderung_${vsnr}_${bsnr}`;
        const timestamp = (new Date()).valueOf();

        const retrievedTimestamp = window.sessionStorage.getItem(localStorageKey);

        if (retrievedTimestamp) {
            const messageModel = new MessageModel(
                'Rückforderung bereits beantragt',
                [
                    `Ein Rückforderungswunsch für Bürgschaft ${bsnr} wurde heute schon einmal ausgelöst, daher
                     ist eine erneute Anforderung erst wieder zu einen späteren Zeitpunkt möglich.`
                ]
            );
            this.messageObservable.notify(messageModel);

            return;
        }

        window.sessionStorage.setItem(localStorageKey, String(timestamp));

        this.buergscheineService.postBuergscheinRueckforderung(vsnr, bsnr).subscribe({
            next: () => {
                const messageModel = new MessageModel(
                    'Rückforderung erfolgreich beantragt',
                    [
                        `Der Auftraggeber wurde darüber informiert, dass Sie die Bürgschaft ${bsnr} zurückgefordert haben.`,
                        'Bei erfolgter Rückgabe wird die Bürgschaft zeitnah ausgebucht.',
                        'Bei einer Ablehnung werden Sie über die Nachrichtenfunktion des Kreditportals über diese informiert.'
                    ]
                );

                this.messageObservable.notify(messageModel);
            },
            error: () => {
                this.rueckforderungLocked = false;
            }
        });
    }

}


